import {
  DeviceLabels,
  type MeetingManager,
} from 'amazon-chime-sdk-component-library-react'
import {
  type MeetingSession,
  MeetingSessionConfiguration,
} from 'amazon-chime-sdk-js'
import { assertEvent, assign, fromPromise, setup } from 'xstate'
import type {
  useFccInitiateOperatorToPassengerCallMutation,
  useFccInitiateOperatorToSafetyDriverCallMutation,
  useFccJoinPassengerCallMutation,
  useFccJoinSafetyDriverCallMutation,
} from '../CallControls/CallControls.hook'

export type CallDirection = 'incoming' | 'outgoing'
export type CallType = 'passenger' | 'safetyDriver'

const initialContext = {
  isMuted: false,
  callDirection: undefined,
  callType: undefined,
  chimeCredentials: undefined,
}

type MachineDependencies = {
  meetingManager: MeetingManager
  meetingSession: MeetingSession | null
  enableMicrophone: () => Promise<void>
  vehicleId: string
  emergencyStopAlertId: string | undefined
  mutations: {
    joinPassengerCallMutation: ReturnType<
      typeof useFccJoinPassengerCallMutation
    >[0]
    joinSafetyDriverCallMutation: ReturnType<
      typeof useFccJoinSafetyDriverCallMutation
    >[0]
    initiateOperatorToPassengerCallMutation: ReturnType<
      typeof useFccInitiateOperatorToPassengerCallMutation
    >[0]
    initiateOperatorToSafetyDriverCallMutation: ReturnType<
      typeof useFccInitiateOperatorToSafetyDriverCallMutation
    >[0]
  }
}

const requestChimeCredentialsActor = (dependencies: MachineDependencies) =>
  fromPromise(
    async ({
      input,
    }: {
      input: {
        callDirection: CallDirection | undefined
        callType: CallType | undefined
      }
    }) => {
      if (input.callDirection === 'incoming') {
        switch (input.callType) {
          case 'passenger': {
            const res = await dependencies.mutations.joinPassengerCallMutation({
              variables: { vehicleId: dependencies.vehicleId },
              onError: error => {
                throw error
              },
            })
            return res.data?.fccJoinPassengerCall
          }
          case 'safetyDriver': {
            const res =
              await dependencies.mutations.joinSafetyDriverCallMutation({
                variables: { vehicleId: dependencies.vehicleId },
                onError: error => {
                  throw error
                },
              })
            return res.data?.fccJoinSafetyDriverCall
          }
          default:
            return undefined
        }
      }
      if (input.callDirection === 'outgoing') {
        switch (input.callType) {
          case 'passenger': {
            const res =
              await dependencies.mutations.initiateOperatorToPassengerCallMutation(
                {
                  variables: {
                    vehicleId: dependencies.vehicleId,
                    emergencyStopAlertId: dependencies.emergencyStopAlertId,
                  },
                  onError: error => {
                    throw error
                  },
                },
              )
            return res.data?.fccInitiateOperatorToPassengerCall
          }
          case 'safetyDriver': {
            const res =
              await dependencies.mutations.initiateOperatorToSafetyDriverCallMutation(
                {
                  variables: { vehicleId: dependencies.vehicleId },
                  onError: error => {
                    throw error
                  },
                },
              )
            return res.data?.fccInitiateOperatorToSafetyDriverCall
          }
          default:
            return undefined
        }
      }
      return undefined
    },
  )

const joinChimeMeetingActor = (dependencies: MachineDependencies) =>
  fromPromise(async ({ input }: { input: string | undefined }) => {
    if (!input) throw new Error('No chime credentials')
    const { meeting, attendee } = JSON.parse(input)
    const meetingSessionConfiguration = new MeetingSessionConfiguration(
      { Meeting: meeting },
      { Attendee: attendee },
    )
    await dependencies.enableMicrophone()
    await dependencies.meetingManager.join(meetingSessionConfiguration, {
      deviceLabels: DeviceLabels.Audio,
    })
    await dependencies.meetingManager.start()
  })

const leaveChimeMeetingActor = (dependencies: MachineDependencies) =>
  fromPromise(async () => {
    dependencies.meetingSession?.audioVideo.stop()
    await dependencies.meetingManager.leave()
  })

export const createCallMachine = (dependencies: MachineDependencies) =>
  setup({
    types: {
      context: {} as {
        isMuted: boolean
        callDirection: CallDirection | undefined
        callType: CallType | undefined
        chimeCredentials: string | undefined
      },
      events: {} as
        | { type: 'INCOMING_CALL_RECEIVED'; callType: CallType }
        | { type: 'OUTGOING_CALL_STARTED'; callType: CallType }
        | { type: 'OPERATOR_PICKED_UP' }
        | { type: 'ALERT_CREATED' }
        | { type: 'OPERATOR_JOINED' }
        | { type: 'CALLEE_JOINED' }
        | { type: 'MUTE_TOGGLED' }
        | { type: 'OPERATOR_HUNG_UP' }
        | { type: 'CHIME_MEETING_ENDED' }
        | { type: 'ALERT_RESOLVED' },
    },
    actors: {
      requestChimeCredentials: requestChimeCredentialsActor(dependencies),
      joinChimeMeeting: joinChimeMeetingActor(dependencies),
      leaveChimeMeeting: leaveChimeMeetingActor(dependencies),
    },
    actions: {
      resetContext: assign(() => initialContext),
      setIncomingCall: assign({
        callDirection: 'incoming',
        callType: ({ event }) => {
          assertEvent(event, 'INCOMING_CALL_RECEIVED')
          return event.callType
        },
      }),
      setOutgoingCall: assign({
        callDirection: 'outgoing',
        callType: ({ event }) => {
          assertEvent(event, 'OUTGOING_CALL_STARTED')
          return event.callType
        },
      }),
      toggleMute: assign({
        isMuted: ({ context }) => {
          if (context.isMuted) {
            dependencies.meetingSession?.audioVideo.realtimeUnmuteLocalAudio()
          } else {
            dependencies.meetingSession?.audioVideo.realtimeMuteLocalAudio()
          }
          return !context.isMuted
        },
      }),
    },
  }).createMachine({
    /** @xstate-layout N4IgpgJg5mDOIC5QAoC2BDAxgCwJYDswBKAOlwgBswBiASQDkBhAeQFkGBxAfUYEEAZflwBKAUUajaANVEARANoAGALqJQABwD2sXABdcm-GpAAPRAEZFAJgAsJKwGZFAVnPOA7FdfObNgDQgAJ6IALQODs4kAJwOABwxUb4AbEmKirE2AL6ZAWhYeISk5FTUzACqACoczJw8AkIAyhW8whVySqpIIFo6+obGZgjmNooOJG6xDuaxXs5WAcEIISNWJGmjw+bDtopRztm5GDgExCQATgRQl6UACqLCvBXMwlw3tIwA0nJcZTcdxj09AYjF1BpYbOZolErI45s5nC4tgtQuYHFESM4okkHDZ3ND3JMkjYkgcQHljoVzpdrgJ7hURKIGsx+DIFCoAdogf1QYh3OZ3CR3KNcYp+Rt3ElkUtHALzElHFYojFJs4kuZSeSCqczmAAI4AVzg+nwUEYeFQYEYOogYHw+nQFFg1AghjAZHwADdNABrN2ak6kHUGo2XM24C1WyC2+2OhAEL2YdB9fAdf5dQHJgYWayRcKY2JwhGucxS1UkQk2KbTKxJdxCqIao5awN6w2wY2m82W63R3AOp1gM5nTRnEjqChJgBmI9QJH9lKDbY7YYjPbtfdj8c0ieTqfZ6c5mZ5CE8YwyqWcsU8xKSiSlYQFrkUIyioprV+sjfyAZIACtNAQly0PgmCaKgoYOhQ1CDsOo7jlOM5zk2P7-oBJrAaB4EmowkFxp625JsCe6dBoh7AlmCCxGk9ixESl7hAxOJSoqqx5lYfLsWqUTTF+FKnKh+BASBYEQRQUHMHcDxPC8ABSNT0O0+6kb05HHls5jolRVjTGqkwwgSUo4pCMyKnKKTmbWvHNn+AGCehwlYaakHUIwAAStCsKIXCeaIFS1KI9CyIpJHdGR3KgIMVGKOMT4QhZKTuIZELljC3HxQlVkobZlzMPquhQLZTlic6rrul6vpId+lICTleUFaJFB4QmhGGMRHIqeFpgWDYXjljYUTXkW8xBIgViKEk9gMWk8rOE4fKZdV2Umrl+WFThxUwSOY4Tro05nLO878UtUArfV2G4VuO5ESoabKVyIIRRY7houMV4IusH3DYsEqQo44TTV4c0OAtpyAbohHYV2rBgGAHYlYQZU+n6yGUmDEOduGYDQ7DlxNQRu43UpoUdQ9XVDCMYxOFYGR7C4X0opM5axMzaqzb4-X7DkZIo6Dgng8uUMw3Dm1wTte0HTzRR8+jK5Y0LuOXS1KaEyFGaqY9QyTAKiTmIqr41l4exSlRdi6zMmKol4aog6QADu6BAiaABiI68FQZy6JGSvULSrQ8GIjzBe190UcMPXROxNNDYZJC3h48rbIo7jwtsNskPbjtQC7ZzrVQYAVJoMm2dQrCVF5TwcBw-BBweJOh0SqxzLYNi0TWVPOIZ0x9diPhmRZwNc4ddsOx22e5zDBdFwQLn1KIXlyQwNd3UeGuoqigrPWiGRUQ4KQdyNCBGSlpnpSkA+HFVpwZ6PI7j-nhfF25HleT5fn0NwAVBWyqthaTYK+AKBw7gIS0XSEKZODgpS1miqMawuw5Q0zxGna+lwx6QQng-aeEl7iPGeFwVyZR34-D+ETNWnV-4DRIA4MaUVoEjEmFKPksQ1gDWZmiS8RIXBp0TGJYCNxhxQB1LAJ0pc2hcArlXJexMQ5qV3pEWi2JdaWHCFeUswwSAt24ribEBYe7cMgnwgRQinRP08t5Oeb8P6BSkWQv+2YxqCkcIkKYzMVj0wQK4Owr5qHUzlHKawJJB6SxIDwighjNCCLgE6bBUk8EEKIb8W60iV5k2UaxJU15KwZGsO41UlMXAuDoqKZ8VhkEj1Qa7d2uhhBwE0BQPKwIfbVz9mIJkLIbG-woh4dEp4CTpCSEWUU95UTokxNiXE+IKyBK5vgTQNp4BdCHsHFJgxZqQl6VRWigySwH2WHsQUA05RjRcJMXEadihgGWerMmqR0Qt2fHEFu0xnr70WMsdI9hDnnjrM+FuacLgmkuFc8h2ZKzjD5NCdi8JEQ7LeTWaKKQFSxCOci5F-zWwhkhpjSMNp1z9mBXYoYopjJjV3jmIswx7zygxOCOUwCERYhhGnGq9lMINQJaHYUVDaywlmDCqU0xfpTXGoDJOutmXHVOmtSCHK1IQlYjiXYtM5jG2igWSYANZpJ3Yuc6WAtMbYw7LK1ecQelXhhDYaOuzqHMIGT1V8WINLwLKZnbObtByex1ErY1qSITolSLvOa8J3EhGpc9ZOPUYRJCvJWZOLqb453QffKepNbGhx6usp58o0gqoPuxAUQpHkDUrIohsQTL6kFCeEyJwifVgmptFCUiRIVOF3gMqUlrISVmcXiHq6rOYXz4sPV1lSPU1NgHUhpnU03Hg4eMThPhxrjN8GosYWIPCWFRLoiI2RshAA */
    context: initialContext,
    initial: 'idle',
    states: {
      idle: {
        entry: 'resetContext',
        on: {
          INCOMING_CALL_RECEIVED: 'ringing',
          OUTGOING_CALL_STARTED: 'initatingChimeMeeting',
        },
      },

      ringing: {
        entry: 'setIncomingCall',
        on: {
          OPERATOR_PICKED_UP: 'requestingChimeCredentials',
          ALERT_RESOLVED: 'idle',
        },
      },

      requestingChimeCredentials: {
        invoke: {
          src: 'requestChimeCredentials',
          input: ({ context }) => ({
            callDirection: context.callDirection,
            callType: context.callType,
          }),
          onDone: {
            target: 'joiningIncomingCall',
            actions: assign({ chimeCredentials: ({ event }) => event.output }),
          },
          onError: 'waitingForAlertResolution',
        },
      },

      joiningIncomingCall: {
        invoke: [
          {
            src: 'joinChimeMeeting',
            input: ({ context }) => context.chimeCredentials,
            onError: 'waitingForAlertResolution',
          },
        ],
        on: {
          OPERATOR_JOINED: 'callInProgress',
          CHIME_MEETING_ENDED: 'waitingForAlertResolution',
        },
      },

      joiningOutgoingCall: {
        invoke: [
          {
            src: 'joinChimeMeeting',
            input: ({ context }) => context.chimeCredentials,
            onDone: { target: 'waitingForCalleeToJoin' },
            onError: 'waitingForAlertResolution',
          },
        ],
      },

      initatingChimeMeeting: {
        entry: 'setOutgoingCall',
        invoke: {
          src: 'requestChimeCredentials',
          input: ({ context }) => ({
            callDirection: context.callDirection,
            callType: context.callType,
          }),
          onDone: {
            target: 'waitingForAlertCreation',
            actions: assign({ chimeCredentials: ({ event }) => event.output }),
          },
          onError: 'waitingForAlertResolution',
        },
      },

      waitingForAlertCreation: {
        on: { ALERT_CREATED: 'joiningOutgoingCall' },
      },

      waitingForCalleeToJoin: {
        on: {
          MUTE_TOGGLED: { actions: 'toggleMute' },
          CALLEE_JOINED: 'callInProgress',
          CHIME_MEETING_ENDED: 'waitingForAlertResolution',
          OPERATOR_HUNG_UP: 'waitingForAlertResolution',
        },
      },

      callInProgress: {
        on: {
          MUTE_TOGGLED: { actions: 'toggleMute' },
          CHIME_MEETING_ENDED: 'waitingForAlertResolution',
          OPERATOR_HUNG_UP: 'waitingForAlertResolution',
        },
      },

      waitingForAlertResolution: {
        invoke: {
          src: 'leaveChimeMeeting',
        },
        on: { ALERT_RESOLVED: 'idle' },
      },
    },
  })
